import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where, orderBy, limit, Timestamp, doc, updateDoc } from 'firebase/firestore';

export interface Introduction {
  id?: string;
  fromUserId: string;
  fromUserName: string;
  toUserId: string; // Persoon aan wie je voorstelt
  recommendedUserId: string; // Persoon die je voorstelt
  recommendedUserName: string;
  recommendedUserPhoto?: string;
  message?: string;
  status: 'pending' | 'accepted' | 'declined';
  createdAt: Date | Timestamp;
}

class IntroductionService {
  private readonly INTRODUCTIONS_COLLECTION = 'introductions';

  /**
   * Stel twee mensen aan elkaar voor
   */
  async createIntroduction(
    fromUserId: string,
    fromUserName: string,
    toUserId: string,
    recommendedUserId: string,
    recommendedUserName: string,
    recommendedUserPhoto?: string,
    message?: string
  ): Promise<string> {
    try {
      // Check if introduction already exists
      const existingQuery = query(
        collection(db, this.INTRODUCTIONS_COLLECTION),
        where('fromUserId', '==', fromUserId),
        where('toUserId', '==', toUserId),
        where('recommendedUserId', '==', recommendedUserId),
        where('status', '==', 'pending')
      );
      const existingSnapshot = await getDocs(existingQuery);
      
      if (!existingSnapshot.empty) {
        throw new Error('Je hebt deze persoon al voorgesteld aan deze gebruiker');
      }

      const introductionData = {
        fromUserId,
        fromUserName,
        toUserId,
        recommendedUserId,
        recommendedUserName,
        recommendedUserPhoto: recommendedUserPhoto || null,
        message: message || `${fromUserName} stelt ${recommendedUserName} aan je voor!`,
        status: 'pending' as const,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, this.INTRODUCTIONS_COLLECTION), introductionData);
      
      // Stuur ook een inbox notificatie
      await addDoc(collection(db, 'users', toUserId, 'inbox'), {
        type: 'introduction',
        fromUserId,
        fromUserName,
        recommendedUserId,
        recommendedUserName,
        recommendedUserPhoto: recommendedUserPhoto || null,
        message: introductionData.message,
        introductionId: docRef.id,
        read: false,
        createdAt: Timestamp.now()
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating introduction:', error);
      throw error;
    }
  }

  /**
   * Haal alle introductions op voor een gebruiker
   */
  async getIntroductionsForUser(userId: string): Promise<Introduction[]> {
    try {
      const q = query(
        collection(db, this.INTRODUCTIONS_COLLECTION),
        where('toUserId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as Introduction[];
    } catch (error) {
      console.error('Error getting introductions:', error);
      return [];
    }
  }

  /**
   * Haal alle introductions op die een gebruiker heeft verstuurd
   */
  async getIntroductionsFromUser(userId: string): Promise<Introduction[]> {
    try {
      const q = query(
        collection(db, this.INTRODUCTIONS_COLLECTION),
        where('fromUserId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as Introduction[];
    } catch (error) {
      console.error('Error getting introductions from user:', error);
      return [];
    }
  }

  /**
   * Update introduction status
   */
  async updateIntroductionStatus(introductionId: string, status: 'accepted' | 'declined'): Promise<void> {
    try {
      await updateDoc(doc(db, this.INTRODUCTIONS_COLLECTION, introductionId), {
        status,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating introduction status:', error);
      throw error;
    }
  }
}

export default new IntroductionService();


